import type { ChangeEvent, CSSProperties, FC, FocusEventHandler, KeyboardEventHandler } from 'react';
import styles from './TimePickerNative.module.scss';
import cn from 'classnames';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { TimeFormat, TimeUtils } from '@/utils/Date.utils';

const validateTime = (time: string, format: TimeFormat): string => {
  if (!TimeUtils.isValidTime(time, format)) {
    ExceptionHandler.captureException(
      new InVivoError(`Invalid time format: ${format}`, {
        cause: time,
        slug: 'time-picker-invalid-format',
      })
    );
    return '';
  }
  return time;
};

export interface TimePickerNativeProps {
  value: string;
  name?: string;
  format?: Exclude<TimeFormat, 'Time_12HR' | 'TimeWithSeconds_12HR'>;
  step?: string;
  min?: string;
  max?: string;
  className?: string;
  style?: CSSProperties;
  onChange?: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  testId?: string;
  autoFocus?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

export const TimePickerNative: FC<TimePickerNativeProps> = ({
  value,
  name = 'time-picker',
  format = TimeFormat.Time_24HR,
  step,
  min,
  max,
  className,
  style,
  onChange,
  onBlur,
  disabled,
  autoFocus,
  testId = 'time-picker',
  onKeyDown,
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const validatedValue = validateTime(newValue, format);
    if (onChange) {
      onChange(validatedValue);
    }
  };

  return (
    <input
      type="time"
      name={name}
      value={value}
      step={step}
      min={min}
      max={max}
      className={cn(styles.timePickerNative, className)}
      style={style}
      onChange={handleChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      autoFocus={autoFocus}
      data-test-component="TimePickerNative"
      data-test-element="input"
      data-testid={testId}
    />
  );
};

export default TimePickerNative;
